import {
  FaxStatusInfo,
  MailStatusInfo,
  mapFaxStatusToDeliveryStatus,
  mapLobStatusToDeliveryStatus,
  normalizeFaxNumber,
} from "./delivery-status-utils";

export interface Address {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zip: string;
}

export interface RecipientData {
  key: string;
  name: string;
  companyName: string | null;
  address: Address | null;
  toFaxNumber: string | null;
  fromFaxNumber: string | null;
  faxSentTimestamp: string | null;
  mailStatus: MailStatusInfo | null;
  faxStatus: FaxStatusInfo | null;
}

export interface SentFax {
  toFaxNumber: string;
  fromFaxNumber: string;
  sentStatus: string;
  initiatedTimestamp?: string | null;
  completedTimestamp?: string | null;
  errorDetails?: string | null;
}

export interface SentLetter {
  recipientName: string;
  address: Address;
  lobStatus: string;
}

export interface Destination {
  name: string;
  fax?: string | null;
  company?: string | null;
}

export interface CcDestination {
  destination: Destination;
}

export interface PatientInsurance {
  providerName?: string | null;
}

export function findFaxRecipientName(
  faxNumber: string,
  destination: Destination | null | undefined,
  ccDestinations: CcDestination[],
): string {
  const normalizedFax = normalizeFaxNumber(faxNumber);

  if (
    destination?.fax &&
    normalizeFaxNumber(destination.fax) === normalizedFax
  ) {
    return destination.name;
  }

  const matchingCc = ccDestinations.find(
    (cc) => normalizeFaxNumber(cc.destination.fax) === normalizedFax,
  );
  if (matchingCc) {
    return matchingCc.destination.name;
  }

  return faxNumber;
}

export function buildRecipientsFromSentItems(
  sentLetters: SentLetter[],
  sentFaxes: SentFax[],
  destination: Destination | null | undefined,
  ccDestinations: CcDestination[],
  patientInsurance: PatientInsurance | null | undefined,
): RecipientData[] {
  const recipientMap = new Map<string, RecipientData>();

  sentLetters.forEach((letter) => {
    const normalizedName = letter.recipientName.toLowerCase().trim();
    const existing = recipientMap.get(normalizedName);

    if (existing) {
      existing.mailStatus = {
        status: mapLobStatusToDeliveryStatus(letter.lobStatus),
        lobStatus: letter.lobStatus,
      };
      if (!existing.address) {
        existing.address = letter.address;
      }
    } else {
      recipientMap.set(normalizedName, {
        key: `recipient-${normalizedName}`,
        name: letter.recipientName,
        companyName: null,
        address: letter.address,
        toFaxNumber: null,
        fromFaxNumber: null,
        faxSentTimestamp: null,
        mailStatus: {
          status: mapLobStatusToDeliveryStatus(letter.lobStatus),
          lobStatus: letter.lobStatus,
        },
        faxStatus: null,
      });
    }
  });

  sentFaxes.forEach((fax) => {
    const faxRecipientName = findFaxRecipientName(
      fax.toFaxNumber,
      destination,
      ccDestinations,
    );
    const normalizedName = faxRecipientName.toLowerCase().trim();
    const existing = recipientMap.get(normalizedName);

    if (existing) {
      existing.faxStatus = {
        status: mapFaxStatusToDeliveryStatus(fax.sentStatus),
        errorDetails: fax.errorDetails ?? undefined,
      };
      existing.toFaxNumber = existing.toFaxNumber ?? fax.toFaxNumber;
      existing.fromFaxNumber = existing.fromFaxNumber ?? fax.fromFaxNumber;
      existing.faxSentTimestamp =
        fax.completedTimestamp ??
        existing.faxSentTimestamp ??
        fax.initiatedTimestamp ??
        null;
    } else {
      recipientMap.set(normalizedName, {
        key: `recipient-${normalizedName}`,
        name: faxRecipientName,
        companyName: null,
        address: null,
        toFaxNumber: fax.toFaxNumber,
        fromFaxNumber: fax.fromFaxNumber,
        faxSentTimestamp:
          fax.completedTimestamp ?? fax.initiatedTimestamp ?? null,
        mailStatus: null,
        faxStatus: {
          status: mapFaxStatusToDeliveryStatus(fax.sentStatus),
          errorDetails: fax.errorDetails ?? undefined,
        },
      });
    }
  });

  const recipients = Array.from(recipientMap.values());

  const normalizedDestinationName = destination?.name?.toLowerCase().trim();
  const normalizedCcNames = ccDestinations.map((cc) =>
    cc.destination.name?.toLowerCase().trim(),
  );

  recipients.sort((a, b) => {
    const aNormalized = a.name.toLowerCase().trim();
    const bNormalized = b.name.toLowerCase().trim();

    const aIsDestination = aNormalized === normalizedDestinationName;
    const bIsDestination = bNormalized === normalizedDestinationName;

    if (aIsDestination && !bIsDestination) return -1;
    if (!aIsDestination && bIsDestination) return 1;

    const aIsCc = normalizedCcNames.includes(aNormalized);
    const bIsCc = normalizedCcNames.includes(bNormalized);

    if (aIsCc && !bIsCc) return -1;
    if (!aIsCc && bIsCc) return 1;

    return 0;
  });

  const firstRecipient = recipients[0];
  if (
    firstRecipient &&
    normalizedDestinationName === firstRecipient.name.toLowerCase().trim()
  ) {
    firstRecipient.companyName =
      destination?.company ?? patientInsurance?.providerName ?? null;
  }

  return recipients;
}
